import { faAngleRight } from "@jewlr/pro-light-svg-icons/faAngleRight"
import {
  Box,
  Button,
  Typography,
  FontAwesomeIcon,
  Divider,
  Flex,
  Picture,
  Image,
} from "@jewlr/storybook/core"
import { Link } from "react-router-dom"
import styled from "styled-components"

import { jewlr_t } from "helpers/application"

import {
  Paragraph,
  Section,
  SectionLeft,
  SectionRight,
  SectionTitle,
} from "./components"
import MainBanner from "./main-banner"

const SectionSubtitle = styled(Typography).attrs((props) => ({
  color: "primary",
  fontSize: "14px",
  letterSpacing: "1.25px",
  mb: "8px",
  ...props,
}))``

const MetalBlock = styled(Flex).attrs((props) => ({
  alignItems: "center",
  flexDirection: { tablet: "column" },
  gutter: { _: 1, tablet: 0 },
  mb: 2,
  textAlign: { tablet: "center" },
  ...props,
}))``
const HowWideSection = styled(Box).attrs((props) => ({
  bg: "polar",
  mb: "3em",
  pb: "20px",
  pt: "20px",
  px: "20px",
  ...props,
}))`
  margin-bottom: 3em;
`

const SubTitles = styled(Typography).attrs((props) => ({
  color: "primary",
  fontSize: "14px",
  letterSpacing: "1.25px",
  mb: "1.3em",
  ...props,
}))``

const StartBtn = styled(Button).attrs((props) => ({
  color: "primary",
  fontSize: "16px",
  fontWeight: "300",
  letterSpacing: { tablet: "0.25px" },
  variant: "text",
  ...props,
}))`
  :hover {
    color: ${(props) => props.theme.colors.primary};
  }
`
const VidBox = styled(Box)`
  height: auto;
  max-width: 100%;
  overflow: hidden;
  padding-bottom: 56.25%;
  position: relative;
`

const HowItsMade = () => (
  <div>
    <MainBanner alt="How Its Made" src="how_its_made" />

    <Box contain mb={9} px={{ _: "10px", tablet: 2 }}>
      <Section>
        <SectionLeft>
          <SectionTitle>How Jewlr Works</SectionTitle>
        </SectionLeft>
        <SectionRight>
          <Paragraph>
            We’ve combined innovative custom design technology with traditional
            hand made manufacturing to deliver high quality{" "}
            {jewlr_t("terms.jewelry")}, created by you.
          </Paragraph>
        </SectionRight>
      </Section>
      <Divider mb={3} />
      <Section>
        <Flex display={{ _: "block", desktop: "flex" }}>
          <Box width={{ desktop: 4 / 6 }}>
            <Box>
              <VidBox mb={1}>
                <iframe
                  allowFullScreen=""
                  frameBorder="0"
                  height="100%"
                  mozallowfullscreen=""
                  src="//player.vimeo.com/video/99177703"
                  style={{ position: "absolute" }}
                  webkitallowfullscreen=""
                  width="100%"
                ></iframe>
              </VidBox>
            </Box>
          </Box>
          <Box ml={{ _: 0, desktop: 3 }} width={{ desktop: 2 / 6 }}>
            <SectionSubtitle>
              Create a memorable item, for any occasion, for anyone.
            </SectionSubtitle>
            <Paragraph>
              Create one-of-a-kind {jewlr_t("terms.jewelry")} that celebrates
              life&apos;s moments! Begin {jewlr_t("terms.personalizing")} with
              your choice of .925 sterling silver, 10K or 14K white, yellow, or
              rose gold. Next, add sparkle by selecting stones from our genuine
              or simulated gemstones or colorful Brilliance Cubic Zirconia
              collections. Choose your stones to represent yourself or your
              loved ones, or to commemorate a birthday, anniversary, or special
              milestone, and complete your unique piece with a custom engraving
              for a special touch.
            </Paragraph>
          </Box>
        </Flex>
      </Section>
    </Box>
    <HowWideSection pt={{ _: "100px", tablet: "20px" }}>
      <Box contain position="relative">
        <Box
          textAlign={{ _: "left", tablet: "right" }}
          width={{ tablet: "50%" }}
        >
          <SectionTitle>Step 1</SectionTitle>
          <SubTitles>Choose a style</SubTitles>
          <Paragraph>
            We have a large selection of rings, charms, pendants and earrings
            for you to choose from. Our {jewlr_t("terms.jewelry")} designers are
            always adding new items, so keep posted!
          </Paragraph>

          <Box
            as="figure"
            left={{ _: "45%", tablet: "55%" }}
            position="absolute"
            top={{ _: "-100%", desktop: "-60%", tablet: "-50%" }}
            width={{ _: "50%", desktop: "22%", tablet: "33%" }}
          >
            <Image
              alt="Ring Wireframe"
              height={{ _: "244px", tablet: "268px" }}
              src={require("images/info/how_its_made/ring_wireframe.png")}
              width={{ _: "205px", tablet: "225px" }}
            />
          </Box>
        </Box>
      </Box>
    </HowWideSection>
    <Box contain>
      <Section px="10px">
        <SectionLeft
          mb={2}
          textAlign={{ tablet: "right" }}
          width={{ tablet: 1 / 4 }}
        >
          <SectionTitle>Step 2</SectionTitle>
          <Box>
            <SubTitles>Choose a metal</SubTitles>
            <Paragraph>
              We offer sterling silver, 10K and 14K yellow, white and rose gold,
              and different combinations of tungsten.
            </Paragraph>
          </Box>
        </SectionLeft>
        <SectionRight width={{ tablet: 3 / 4 }}>
          <Flex
            flexDirection={{ _: "column", tablet: "row" }}
            gutter={{ tablet: 1 }}
          >
            <MetalBlock>
              <Box mb={1}>
                <Picture
                  alt="Silver"
                  fallback={require("images/info/how_its_made/silver_2x.png")}
                  srcSet={[
                    {
                      height: "140",
                      media: "(max-width: 767px)",
                      sizes: "350w",
                      srcSet: `${require("images/info/how_its_made/silver.png")} 350w`,
                      width: "140",
                    },
                    {
                      height: "100",
                      media: "(min-width: 768px)",
                      sizes: "100px",
                      srcSet: `
              ${require("images/info/how_its_made/silver.png")} 141w,
              ${require("images/info/how_its_made/silver_2x.png")} 285w
              `,
                      width: "100",
                    },
                  ]}
                />
              </Box>
              <Box>
                <SectionSubtitle>Silver</SectionSubtitle>
                <Paragraph>
                  Our silver is made from 92.5% sterling silver. The other 7.5%
                  is a copper alloy used to increase strength.
                </Paragraph>
              </Box>
            </MetalBlock>
            <MetalBlock>
              <Box mb={1}>
                <Picture
                  alt="Gold"
                  fallback={require("images/info/how_its_made/gold_2x.png")}
                  srcSet={[
                    {
                      height: "140",
                      media: "(max-width: 767px)",
                      sizes: "350w",
                      srcSet: `${require("images/info/how_its_made/gold.png")} 350w`,
                      width: "140",
                    },
                    {
                      height: "100",
                      media: "(min-width: 768px)",
                      sizes: "100px",
                      srcSet: `
              ${require("images/info/how_its_made/gold.png")} 141w,
              ${require("images/info/how_its_made/gold_2x.png")} 285w
              `,
                      width: "100",
                    },
                  ]}
                />
              </Box>
              <Box>
                <SectionSubtitle>Gold</SectionSubtitle>
                <Paragraph>
                  Gold is one of the most precious metals. We offer 10K and 14K
                  yellow, white and rose gold.
                </Paragraph>
              </Box>
            </MetalBlock>
            <MetalBlock>
              <Box mb={1}>
                <Picture
                  alt="Tungsten"
                  fallback={require("images/info/how_its_made/tungsten_2x.png")}
                  srcSet={[
                    {
                      height: "140",
                      media: "(max-width: 767px)",
                      sizes: "350w",
                      srcSet: `${require("images/info/how_its_made/tungsten.png")} 350w`,
                      width: "140",
                    },
                    {
                      height: "100",
                      media: "(min-width: 768px)",
                      sizes: "100px",
                      srcSet: `
              ${require("images/info/how_its_made/tungsten.png")} 141w,
              ${require("images/info/how_its_made/tungsten_2x.png")} 285w
              `,
                      width: "100",
                    },
                  ]}
                />
              </Box>
              <Box>
                <SectionSubtitle>Tungsten</SectionSubtitle>
                <Paragraph>
                  Some of our items are available in varieties of tungsten -
                  which is one of the world&apos; s strongest metals.
                </Paragraph>
              </Box>
            </MetalBlock>
          </Flex>
        </SectionRight>
      </Section>
    </Box>

    <Box contain mb={3} px={{ _: "10px", desktop: 0 }}>
      <Divider bg="grey.10" mb={{ _: 5, tablet: 9 }} mt={3} />

      <Flex display={{ _: "block", desktop: "flex" }} gutter={{ desktop: 2 }}>
        <Box mr="-10px" position="relative" width={{ desktop: 2 / 3 }}>
          <Picture
            alt="Gemstones"
            fallback={require("images/info/how_its_made/loosestones.png")}
            srcSet={[
              {
                height: "476",
                media: "(max-width: 767px)",
                sizes: "100vw",
                srcSet: `${require("images/info/how_its_made/loosestones_mobile.png")} 767w`,
                width: "436",
              },
              {
                height: "480",
                media: "(min-width: 768px)",
                sizes: "330px",
                srcSet: `
              ${require("images/info/how_its_made/loosestones.png")} 612w,
              ${require("images/info/how_its_made/loosestones_2x.png")} 1224w
              `,
                width: "670",
              },
            ]}
            width={{ _: "100%" }}
          />
          <Box
            position="absolute"
            textAlign={{ desktop: "right" }}
            top="10px"
            width={{ _: "75%", desktop: "45%", tablet: "55%" }}
          >
            <SectionTitle textAlign={{ desktop: "right", tablet: "left" }}>
              Step 3
            </SectionTitle>
            <Box>
              <SubTitles>Choose a stone</SubTitles>
              <Paragraph>
                We offer genuine and simulated gemstones, along with our
                Brilliance Cubic Zirconia. We also have a beautiful collection
                of sparkling diamonds.
              </Paragraph>
            </Box>
          </Box>
        </Box>
        <Box width={{ desktop: 1 / 3 }}>
          <SectionSubtitle>Genuine Gemstones</SectionSubtitle>
          <Paragraph mb={2}>
            Genuine gemstones form within the earth over millions of years and
            are then mined and treated to enhance their beauty and natural
            variations. Genuine gemstones and birthstones not only add value to
            your {jewlr_t("terms.jewelry")} but also make each piece unique.
          </Paragraph>
          <SectionSubtitle textAlign="left">Diamonds</SectionSubtitle>
          <Paragraph mb={2}>
            Diamonds are the most valuable stones and are practically
            unbreakable. Their quality is based on {jewlr_t("terms.color")},
            clarity, cut and carat weight.
          </Paragraph>
          <SectionSubtitle textAlign="left">
            Simulated Gemstones & Brilliance Premium Collection
          </SectionSubtitle>
          <Paragraph mb={2}>
            Simulated gemstones, including our Brilliance Collection, are
            created in laboratories to emulate the {jewlr_t("terms.color")} of
            genuine gemstones. They provide a quality stone alternative with a
            choice of vibrant {jewlr_t("terms.color")}s and sparkling cuts at a
            fraction of the cost.
          </Paragraph>
        </Box>
      </Flex>
    </Box>

    <Box contain px={{ _: "10px", tablet: 0 }}>
      <Divider bg="grey.10" mb={{ _: 4, tablet: 7 }} mt={3} />
    </Box>

    <Flex contain display={{ _: "block", tablet: "flex" }} mb={3}>
      <Flex alignItems="center" px="10px" width={{ tablet: 1 / 2 }}>
        <Box order={{ tablet: 2 }} width={{ tablet: 1 / 2 }}>
          <SectionLeft ml="auto" width="100%">
            <SectionTitle textAlign="left">Step 4</SectionTitle>
            <Box>
              <SubTitles>Choose an engraving</SubTitles>
              <Typography as="p">
                We offer four fonts for you to choose from when engraving your
                special piece.
              </Typography>
            </Box>
          </SectionLeft>
        </Box>
        <Box order={{ tablet: 1 }} width={{ tablet: 1 / 2 }}>
          <Image
            alt="Engraving Fonts"
            display={{ _: "none", tablet: "block" }}
            height="209px"
            mb={1}
            src={require("images/info/how_its_made/fonts.svg")}
            style={{ inlineSize: "fit-content" }}
            width="120px"
          />
        </Box>
      </Flex>
      <Box px={{ _: 6, desktop: 9 }} width={{ tablet: 1 / 2 }}>
        <Picture
          alt="Engraving"
          fallback={require("images/info/how_its_made/jwl0018.jpg")}
          srcSet={[
            {
              height: "223",
              media: "(max-width: 767px)",
              sizes: "100vw",
              srcSet: `${require("images/info/how_its_made/jwl0018_mobile.jpg")} 360w`,
              width: "360px",
            },
            {
              height: "216",
              media: "(min-width: 768px)",
              sizes: "330px",
              srcSet: `
              ${require("images/info/how_its_made/jwl0018.jpg")} 347w,
              ${require("images/info/how_its_made/jwl0018_2x.jpg")} 1050w
              `,
              width: "347",
            },
          ]}
        />
      </Box>
    </Flex>
    <HowWideSection mb={5} pt={5}>
      <Flex contain display={{ _: "block", desktop: "flex" }}>
        <Box width={{ desktop: 4 / 6 }}>
          <Box>
            <VidBox mb={3}>
              <iframe
                allowFullScreen=""
                frameBorder="0"
                height="100%"
                mozallowfullscreen=""
                src="//player.vimeo.com/video/99177703"
                style={{ position: "absolute" }}
                webkitallowfullscreen=""
                width="100%"
              ></iframe>
            </VidBox>
          </Box>
        </Box>
        <Box ml={{ _: 0, desktop: "110px" }} width={{ desktop: 2 / 6 }}>
          <SectionLeft ml="auto" width="100%">
            <SectionTitle textAlign="left">Step 5</SectionTitle>
            <Box>
              <SubTitles>Custom manufacturing</SubTitles>
              <Paragraph>
                Once all your selections are made, we print your item using a 3D
                printer, then we cast your metal, set your stones and laser
                engrave your message. Our items are stamped with our trademark
                &lsquo;TSD&rsquo;. When your item is ready, we thoroughly
                inspect it to ensure perfection. After it passes our quality
                control process, we package your item in our custom, beautiful
                packaging and ship it to your door.
              </Paragraph>
            </Box>
          </SectionLeft>
        </Box>
      </Flex>
    </HowWideSection>
    <Box contain mb={3} px="10px" textAlign="center">
      <Paragraph>
        We take pride in the items that we produce. That&apos; s why we go above
        and beyond to make sure that the recipient of every Jewlr piece is
        happy. If you have any questions, comments or concerns, we want to hear
        from you.
      </Paragraph>
      <SectionSubtitle color="black" fontSize="16px">
        CONTACT US
      </SectionSubtitle>
      {[
        {
          label: sl.config.customer_service_email,
          link: `mailto:${sl.config.customer_service_email}`,
        },
        {
          label: sl.config.customer_service_toll_free,
          link: `tel:${sl.config.customer_service_toll_free}`,
        },
      ].map((cs, i) => (
        <Typography
          as="a"
          color="primary"
          display="block"
          fontSize="16px"
          href={cs.link}
          key={`cs${i}`}
          lineHeight="1.75"
        >
          {cs.label}
        </Typography>
      ))}
      <Divider bg="grey.10" mb={4} mt={3} />
      <SectionSubtitle color="black">
        Go ahead, create something special.
      </SectionSubtitle>
      <StartBtn
        data-lc="how-its-made"
        forwardedAs={Link}
        to={`/${jewlr_t("terms.personalized")}-${jewlr_t("terms.jewelry")}`}
      >
        START {jewlr_t("terms.personalizing").toUpperCase()}{" "}
        <FontAwesomeIcon icon={faAngleRight} ml="4px" />
      </StartBtn>
    </Box>
  </div>
)

export default HowItsMade
